import * as CANNON from 'cannon-es';
import CannonDebugger from 'cannon-es-debugger';
import * as THREE from 'three';

// --- SIMPLIFIED: Define Collision Groups ---
const COLLISION_GROUPS = {
    DEFAULT: 1,
    CONTROLLER: 2,
    BUTTON: 4,
    ASTEROID: 8,
    PLAYER: 16,
    BULLET: 32,
    COLLECTABLE: 64,
    WALL: 128
};

class SpacePiratesPhysics {
    physicsWorld;
    cannonDebugger;
    physicsObjects = [];
    controllerMaterial;
    buttonMaterial;
    playerBody;
    bodiesToRemove = [];
    controllerBodies = [];
    useDebugger = false;

    constructor(scene, onPlayerHit, onBulletHit, useDebugger = false) {
        this.useDebugger = useDebugger;
        this.setupPhysics(scene, onPlayerHit, onBulletHit);
    }

    setupPhysics(scene, onPlayerHit, onBulletHit) {
        this.physicsWorld = new CANNON.World({
            gravity: new CANNON.Vec3(0, 0, 0), // No gravity in space
        });

        if (this.useDebugger) {
            this.cannonDebugger = new CannonDebugger(scene, this.physicsWorld, {});
        }

        this.controllerMaterial = new CANNON.Material('controller');
        this.buttonMaterial = new CANNON.Material('button');

        const controllerButtonContactMaterial = new CANNON.ContactMaterial(
            this.controllerMaterial,
            this.buttonMaterial,
            { friction: 0.1, restitution: 0.0 }
        );
        this.physicsWorld.addContactMaterial(controllerButtonContactMaterial);

        this.createPlayerBody(onPlayerHit);
        this.onBulletHit = onBulletHit;
    }

    createPlayerBody(onPlayerHit) {
        const playerShape = new CANNON.Box(new CANNON.Vec3(0.5, 0.5, 0.5));
        this.playerBody = new CANNON.Body({
            mass: 1, // DYNAMIC bodies need mass
            shape: playerShape,
            type: CANNON.Body.DYNAMIC,
            collisionFilterGroup: COLLISION_GROUPS.PLAYER,
            collisionFilterMask: COLLISION_GROUPS.ASTEROID | COLLISION_GROUPS.COLLECTABLE | COLLISION_GROUPS.WALL
        });
        this.physicsWorld.addBody(this.playerBody);

        this.playerBody.addEventListener('collide', (event) => {
            const group = event.body.collisionFilterGroup;
            if (group === COLLISION_GROUPS.ASTEROID) {
                if (!this.bodiesToRemove.includes(event.body)) {
                    this.bodiesToRemove.push(event.body);
                    onPlayerHit(event, 'asteroid');
                }
            } else if (group === COLLISION_GROUPS.WALL) {
                onPlayerHit(event, 'wall');
            }
        });
    }

    createAsteroid(mesh) {
        const asteroidShape = new CANNON.Box(new CANNON.Vec3(0.5, 0.5, 0.5));
        const asteroidBody = new CANNON.Body({
            mass: 0, // Static body
            shape: asteroidShape,
            type: CANNON.Body.STATIC,
            position: new CANNON.Vec3().copy(mesh.position),
            collisionFilterGroup: COLLISION_GROUPS.ASTEROID,
            collisionFilterMask: COLLISION_GROUPS.PLAYER | COLLISION_GROUPS.BULLET
        });
        this.physicsWorld.addBody(asteroidBody);
        return asteroidBody;
    }

    createBullet(mesh, initialVelocity) {
        const bulletShape = new CANNON.Box(new CANNON.Vec3(0.05, 0.05, 0.5)); // Long and thin
        const bulletBody = new CANNON.Body({
            mass: 0.1,
            shape: bulletShape,
            position: new CANNON.Vec3().copy(mesh.position),
            velocity: initialVelocity,
            collisionFilterGroup: COLLISION_GROUPS.BULLET,
            collisionFilterMask: COLLISION_GROUPS.ASTEROID
        });

        bulletBody.addEventListener('collide', (event) => {
            if (event.body.collisionFilterGroup === COLLISION_GROUPS.ASTEROID) {
                // Only process the collision if both bodies haven't been marked for removal yet.
                if (!this.bodiesToRemove.includes(event.body) && !this.bodiesToRemove.includes(bulletBody)) {
                    this.bodiesToRemove.push(event.body); // Mark asteroid for removal
                    this.bodiesToRemove.push(bulletBody); // Mark bullet for removal
                    this.onBulletHit(event.body, bulletBody); // Process the hit
                }
            }
        });

        this.physicsWorld.addBody(bulletBody);
        return bulletBody;
    }


    createCockpitButtonPhysics(mesh, pos, rot, onClick, onHold, onRelease, buttonSize, initialButtonYOffset, sunkDepth) {
        // --- Button Base (Static) ---
        const baseShape = new CANNON.Box(new CANNON.Vec3(0.05, 0.0125, 0.05));
        const buttonBaseBody = new CANNON.Body({ mass: 0, shape: baseShape, position: new CANNON.Vec3().copy(pos) });
        this.physicsWorld.addBody(buttonBaseBody);

        // --- Pushable Button (Dynamic) ---
        const initialButtonPosition = new CANNON.Vec3(pos.x, pos.y + initialButtonYOffset, pos.z);

        const buttonShape = new CANNON.Box(new CANNON.Vec3(buttonSize.width / 2, buttonSize.height / 2, buttonSize.depth / 2));
        const buttonBody = new CANNON.Body({
            mass: 0,
            type: CANNON.Body.KINEMATIC,
            shape: buttonShape,
            position: initialButtonPosition,
            collisionFilterGroup: COLLISION_GROUPS.BUTTON,
            collisionFilterMask: 0, // Kinematic bodies don't need masks for this
        });
        this.physicsWorld.addBody(buttonBody);

        const physicsObject = { mesh, body: buttonBody, type: 'button', isMousePressed: false, size: buttonSize };
        this.physicsObjects.push(physicsObject);

        // --- Button Press Logic ---
        this.addPressDetection(physicsObject, pos, initialButtonYOffset, onClick, onHold, onRelease, sunkDepth);

        return physicsObject;
    }

    addPressDetection(physicsObject, basePosition, initialButtonYOffset, onClick, onHold, onRelease, sunkDepth) {
        const buttonBody = physicsObject.body;
        const buttonMesh = physicsObject.mesh;
        const buttonSize = physicsObject.size;
        let isPressed = false;
        const redColor = new THREE.Color(0xff0000);
        const greenColor = new THREE.Color(0x00ff00);

        // The button's highest point (at rest position of its center)
        const maxY = basePosition.y + initialButtonYOffset;

        // Calculate the button's lowest point (minY) so that its top surface is always at the same height when pressed.
        const baseHeight = 0.025; // The height of the button's housing
        const finalPokeAmount = 0.0025; // How much the button top should poke out of the base when fully pressed
        const baseTopY = basePosition.y + (baseHeight / 2);
        const finalButtonTopY = baseTopY + finalPokeAmount;
        const minY = finalButtonTopY - (buttonSize.height / 2); // Center of the button when its top is at the final position

        const pressDepth = maxY - minY;

        this.physicsWorld.addEventListener('postStep', () => {
            let targetY = maxY;
            let pressValue = 0;

            // if (!physicsObject.isBeingPressedByXR) {
            //     return;targetY = maxY; // Press the button
            // }

            // Check for XR controller interaction
            let isHandPressing = false;
            let handPressTargetY = maxY; // Default to resting position

            if (this.controllerBodies.length > 0) {
                let lowestControllerY = Infinity;
                const buttonTopY = maxY + buttonSize.height / 2; // Top of button at rest

                for (const controllerBody of this.controllerBodies) {
                    const controllerPos = controllerBody.position;
                    const buttonPos = buttonBody.position; // Current button center

                    // Check if controller is horizontally aligned
                    if (Math.abs(controllerPos.x - buttonPos.x) < (buttonSize.width / 2) *1.3&&
                        Math.abs(controllerPos.z - buttonPos.z) < (buttonSize.width / 2) *1.3) {

                        // Check if controller is low enough to interact with the top of the button
                        if (controllerPos.y < buttonTopY) {
                            isHandPressing = true;
                            // Find the lowest controller y-position that is pressing
                            lowestControllerY = Math.min(lowestControllerY, controllerPos.y);
                        }
                    }
                }

                if (isHandPressing) {
                    // The button's new center should be such that its top aligns with the controller's center.
                    handPressTargetY = lowestControllerY - (buttonSize.height / 2);
                }
            }


            if (isHandPressing) {
                // The button's target is the calculated hand press position, clamped between min and max
                targetY = Math.max(minY, Math.min(maxY, handPressTargetY));
            } else if (physicsObject.isMousePressed) {
                targetY = minY;
            }

            // Set the button's position directly for instant response
            buttonBody.position.y = targetY;

            if (pressDepth > 0) {
                pressValue = (maxY - buttonBody.position.y) / pressDepth;
                pressValue = Math.max(0, Math.min(1, pressValue));
            }

            buttonMesh.material.color.copy(redColor).lerp(greenColor, pressValue);

            if (onHold) {
                onHold(pressValue > 0.01 ? pressValue : 0);
            }

            const currentPressedState = pressValue > 0.8;

            if (currentPressedState && !isPressed) {
                isPressed = true;
                if (onClick) onClick();
            } else if (!currentPressedState && isPressed) {
                isPressed = false;
                if (onRelease) onRelease();
            }
        });
    }

    createControllerPhysicsBody() {
        const controllerShape = new CANNON.Sphere(0.05);
        const physicsBody = new CANNON.Body({
            mass: 0,
            type: CANNON.Body.KINEMATIC,
            shape: controllerShape,
            collisionFilterGroup: COLLISION_GROUPS.CONTROLLER,
            collisionFilterMask: 0, // This body is a sensor, it doesn't need to collide
        });
        this.physicsWorld.addBody(physicsBody);
        this.controllerBodies.push(physicsBody);
        return physicsBody;
    }

    removeControllerPhysicsBody(physicsBody) {
        if (physicsBody) {
            this.physicsWorld.removeBody(physicsBody);
            const index = this.controllerBodies.indexOf(physicsBody);
            if (index > -1) {
                this.controllerBodies.splice(index, 1);
            }
        }
    }

    createWall(mesh) {
        const { width, height, depth } = mesh.geometry.parameters;
        const wallShape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
        const wallBody = new CANNON.Body({
            mass: 0, // Static body
            shape: wallShape,
            type: CANNON.Body.STATIC,
            position: new CANNON.Vec3().copy(mesh.position),
            collisionFilterGroup: COLLISION_GROUPS.WALL,
            collisionFilterMask: COLLISION_GROUPS.PLAYER
        });
        this.physicsWorld.addBody(wallBody);
        return wallBody;
    }

    update(deltaTime) {
        if (this.physicsWorld) {
            this.physicsWorld.step(1 / 60, deltaTime, 3);
        }

        // Safely remove bodies after the physics step
        if (this.bodiesToRemove.length > 0) {
            for (const body of this.bodiesToRemove) {
                this.physicsWorld.removeBody(body);
            }
            this.bodiesToRemove = []; // Clear the array
        }

        if (this.useDebugger) {
            this.cannonDebugger.update();
        }
    }
}

export { SpacePiratesPhysics, COLLISION_GROUPS };
